import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../../assets/whitelogo.png';

// Custom Social Icons (not available in lucide-react)
const LinkedinIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const TwitterIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
  </svg>
);

const YoutubeIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
    <path d="m10 8 6 4-6 4z" />
  </svg>
);

const footerLinks = {
  Product: [
    { name: 'Features', href: '/#features' },
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'FAQ', href: '/#faq' },
    { name: 'Pricing', href: '/#pricing' },
  ],
  // Company: [
  //   { name: 'About Us', href: '#' },
  //   { name: 'Careers', href: '#' },
  //   { name: 'Blog', href: '#' },
  //   { name: 'Contact', href: '#' },
  // ],
  Legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Cookie Policy', href: '#' },
  ],
};

const socialLinks = [
  { Icon: TwitterIcon, href: '#', label: 'Twitter' },
  { Icon: LinkedinIcon, href: '#', label: 'LinkedIn' },
  { Icon: YoutubeIcon, href: '#', label: 'YouTube' },
];

const FooterLink = ({ link }) => {
  const isInternalPage = link.href.startsWith('/') && !link.href.includes('#');

  if (isInternalPage) {
    return (
      <Link
        to={link.href}
        className="text-sm text-slate-500 hover:text-purple-600 transition-colors"
      >
        {link.name}
      </Link>
    );
  }

  const handleClick = (e) => {
    if (link.href.includes('#')) {
      e.preventDefault();
      const id = link.href.split('#')[1];
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <a
      href={link.href}
      onClick={handleClick}
      className="text-sm text-slate-500 hover:text-purple-600 transition-colors"
    >
      {link.name}
    </a>
  );
};

const Footer = () => (
  <footer className="relative pt-24 pb-8 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
    {/* Background */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full orb-purple opacity-30" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full orb-indigo opacity-30" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Top Section */}
      <div className="grid lg:grid-cols-2 gap-12 mb-16">
        {/* Brand */}
        <div className="lg:pr-12">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="w-12 h-12 rounded-xl overflow-hidden bg-white shadow-lg shadow-purple-500/10 border border-purple-100"
            >
              <img
                src={logo}
                alt="TeleValley Logo"
                className="w-full h-full object-contain p-1"
              />
            </motion.div>
            <span className="text-xl font-bold text-slate-900">
              Tele<span className="text-gradient">Valley</span>
            </span>
          </Link>

          <p className="text-slate-600 max-w-md mb-6 leading-relaxed">
            The complete solution for telecalling teams. Manage leads, track calls, and boost conversions with powerful automation and real-time analytics.
          </p>

          {/* Contact Info */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-slate-500">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Phone className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Phone</p>
                <a href="tel:+917066333961" className="text-sm font-medium text-slate-700 hover:text-purple-600 transition-colors">
                  +91 7066333961
                </a>
              </div>
            </div>
            {/* <div className="flex items-center gap-3 text-slate-500">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Email</p>
                <a href="mailto:contact@televalley.com" className="text-sm font-medium text-slate-700 hover:text-purple-600 transition-colors">
                  contact@televalley.com
                </a>
              </div>
            </div> */}
            <div className="flex items-center gap-3 text-slate-500">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Location</p>
                <span className="text-sm font-medium text-slate-700">Nashik, India</span>
              </div>
            </div>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-3 gap-8 lg:gap-32">
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-slate-900 mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter Section */}
      {/* <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-8 mb-12 border border-purple-100">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Stay updated</h3>
            <p className="text-slate-600">Get the latest news and updates from TeleValley.</p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 md:w-64 px-4 py-3 rounded-xl bg-white border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 text-sm"
            />
            <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all">
              Subscribe
            </button>
          </div>
        </div>
      </div> */}

      {/* Divider */}
      <div className="border-t border-purple-100 pt-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Copyright */}
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} TeleValley. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => {
              const IconComponent = social.Icon;
              return (
                <motion.a
                  key={social.label}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 rounded-xl bg-white border border-purple-100 flex items-center justify-center text-slate-500 hover:text-purple-600 hover:border-purple-200 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300"
                  aria-label={social.label}
                >
                  <IconComponent className="w-5 h-5" />
                </motion.a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;